import { usePermissionsStore } from "../../../permissions/permissions_store";
import React, { useState, useEffect, useMemo, useCallback, useContext } from "react";
import UIkit from "uikit";
import { Flex } from "uikit-react";
import { useCookies } from "react-cookie";

import { hasOpsSubscription } from "../../../common/OpsSubscriptionRequired";
import RecordsPerPage from "../../../common/RecordsPerPage";
import Pagination from "../../../common/Pagination";
import StorageStockAdjust from "./add";
import { AppContexts } from "../../../../providers";
import RentalsService from "../../../../services/rentals.service";
import ReservationsService from "../../../../services/reservations.service";
import StorageService from "../../../../services/storage.service";
import WorkOrderService from "../../../../services/workorder.service";
import StorageMenu from "../menu";
import {
  DEFAULT_RPP,
  INITIAL_STOCK,
  STOCK_STATUS_OPTIONS,
  availableQuantity,
  formatCurrency,
  formatDateTime,
  inventoryValue,
  labelFor,
  optionName,
  stockStatus,
} from "../data";

const statusBadge = (row) => {
  const status = stockStatus(row);
  if (status === "out") return <span className="uk-badge uk-background-danger">Out</span>;
  if (status === "critical") return <span className="uk-badge uk-background-danger">Critical</span>;
  if (status === "low") return <span className="uk-badge uk-background-warning">Low</span>;
  return <span className="uk-badge">OK</span>;
};

const dateOnly = (date) => date.toISOString().slice(0, 10);

const formatReservationDate = (dateValue) => {
  if (!dateValue) return "";
  const [year, month, day] = String(dateValue).slice(0, 10).split("-");
  return year && month && day ? `${month}/${day}/${year}` : "";
};

const reservationLabel = (reservation) => {
  const guestName = [reservation.first_name, reservation.last_name].filter(Boolean).join(" ");
  const reservationId = reservation.reservation_id || reservation.reservation_code || reservation.native_id || reservation.guest_reservation_id;
  return [
    reservationId,
    guestName,
    reservation.pms_id || reservation.rental_name,
    formatReservationDate(reservation.check_in),
  ]
    .filter(Boolean)
    .join(" - ");
};

const taskLabel = (task) => {
  return [task.work_order_number || task.id, task.title || task.summary || task.name]
    .filter(Boolean)
    .join(" - ");
};

const movementDestination = (row) => {
  if (row.last_to_property_pms_id) return row.last_to_property_pms_id;
  if (row.last_to_location_name) return row.last_to_location_name;
  if (row.last_to_location_id) return `Location #${row.last_to_location_id}`;
  if (row.last_to_rental_id) return `Property #${row.last_to_rental_id}`;
  return "-";
};

export default function StorageStockIndex() {
  const { canEdit } = usePermissionsStore();
  const canManageInventory = canEdit("operations.inventory");
  const [cookies] = useCookies(["records-per-page"]);
  const { user: account } = useContext(AppContexts.AuthContext);
  const opsSubscriptionLocked = !hasOpsSubscription(account);
  const recordsPerPage =
    Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  const [stock, setStock] = useState(INITIAL_STOCK);
  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [properties, setProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [stockSource, setStockSource] = useState("");
  const [criteria, setCriteria] = useState({
    search: "",
    location_id: "",
    status: "",
    page: 0,
  });
  const [editingEntry, setEditingEntry] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const loadStock = useCallback(async () => {
    try {
      const resp = await StorageService.listStock({ page: 0, per: 1000 });
      const stockRows = resp.data?.paginated || (Array.isArray(resp.data) ? resp.data : []);
      setStock(stockRows);
      setStockSource(stockRows.length ? "balances" : "empty");
    } catch (err) {
      setStockSource("error");
      UIkit.notification("Failed to load stock levels.", "error");
    }
  }, []);

  const loadChoices = useCallback(async () => {
    try {
      const [locationsResp, itemsResp] = await Promise.all([
        StorageService.listLocations({ per: 1000 }),
        StorageService.listItems({ per: 1000 }),
      ]);
      setLocations(locationsResp.data?.paginated || []);
      setItems(itemsResp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load stock item choices.", "error");
    }
  }, []);

  const loadReferenceChoices = useCallback(async () => {
    const today = new Date();
    const start = new Date(today);
    start.setDate(start.getDate() - 30);
    const end = new Date(today);
    end.setDate(end.getDate() + 90);

    try {
      const [rentalsResp, reservationsResp, tasksResp] = await Promise.all([
        RentalsService.get().catch(() => ({ data: [] })),
        ReservationsService.searchReservation({
          start_date: dateOnly(start),
          end_date: dateOnly(end),
          preset: "staying-between",
          filters: "standard,owners",
          categories: ["STA", "OWN"],
          page: 0,
          per_page: 500,
        }).catch(() => ({ data: { reservations: [] } })),
        WorkOrderService.getWorkOrders(undefined, { page: 0, per: 500, sort: "id" }).catch(() => ({ data: { paginated: [] } })),
      ]);

      setProperties(Array.isArray(rentalsResp.data) ? rentalsResp.data : []);
      setReservations(reservationsResp.data?.reservations || []);
      setTasks(tasksResp.data?.paginated || []);
    } catch (err) {
      UIkit.notification("Failed to load stock action reference choices.", "error");
    }
  }, []);

  useEffect(() => {
    document.title = "Stock Levels - RueBaRue";
    loadChoices();
    if (canManageInventory) loadReferenceChoices();
    loadStock();
  }, [canManageInventory, loadChoices, loadReferenceChoices, loadStock]);

  const propertyOptions = useMemo(() => (
    properties.map((property) => ({
      label: [property.pms_id || String(property.rental_id), property.address]
        .filter(Boolean)
        .join(" - "),
      value: String(property.rental_id),
      pms_id: property.pms_id || "",
    }))
  ), [properties]);

  const reservationOptions = useMemo(() => (
    reservations.map((reservation) => ({
      label: reservationLabel(reservation),
      value: String(reservation.guest_reservation_id),
      rental_id: reservation.rental_id ? String(reservation.rental_id) : "",
      pms_id: reservation.pms_id || "",
    }))
  ), [reservations]);

  const taskOptions = useMemo(() => (
    [...tasks].sort((left, right) => {
      const leftNumber = Number(left.work_order_number || left.id || 0);
      const rightNumber = Number(right.work_order_number || right.id || 0);
      if (leftNumber !== rightNumber) return leftNumber - rightNumber;
      return String(left.title || left.summary || left.name || "").localeCompare(String(right.title || right.summary || right.name || ""));
    }).map((task) => ({
      label: taskLabel(task),
      value: String(task.id),
      rental_id: task.rental_id ? String(task.rental_id) : "",
    }))
  ), [tasks]);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
      page: ["search", "location_id", "status"].includes(key)
        ? 0
        : prev.page,
    }));
  }, []);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setCriteria((prev) => ({ ...prev, page: newPageIndex }));
  }, []);

  const enrichedStock = useMemo(() => {
    return stock.map((row) => {
      const location = locations.find((loc) => String(loc.id) === String(row.location_id)) || {};
      const item = items.find((storageItem) => String(storageItem.id) === String(row.item_id)) || {};
      return {
        ...row,
        branch_id: row.branch_id || location.branch_id,
        location_name: row.location_name || location.name,
        location_code: row.location_code || location.code,
        item_name: row.item_name || item.name,
        sku: row.sku || item.sku,
        unit: row.unit || item.unit,
        unit_cost: row.unit_cost ?? item.unit_cost,
        replacement_cost: row.replacement_cost ?? item.replacement_cost,
        minimum_quantity: row.minimum_quantity ?? item.minimum_quantity ?? item.default_reorder_point,
        reorder_quantity: row.reorder_quantity ?? item.reorder_quantity ?? item.default_par_level,
      };
    });
  }, [stock, locations, items]);

  const filtered = useMemo(() => {
    return enrichedStock.filter((row) => {
      if (criteria.location_id && String(row.location_id || "") !== criteria.location_id) return false;
      if (criteria.status && stockStatus(row) !== criteria.status) return false;

      if (criteria.search) {
        const query = criteria.search.toLowerCase();
        const haystack = `${row.item_name || ""} ${row.sku || ""} ${
          row.location_name || ""
        } ${row.location_code || ""
        }`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }

      return true;
    });
  }, [enrichedStock, criteria]);

  const sorted = useMemo(() => {
    return [...filtered].sort((left, right) => {
      const leftTime = left.last_movement_at ? new Date(left.last_movement_at).getTime() : 0;
      const rightTime = right.last_movement_at ? new Date(right.last_movement_at).getTime() : 0;
      if (rightTime !== leftTime) return rightTime - leftTime;
      return String(left.item_name || "").localeCompare(String(right.item_name || ""));
    });
  }, [filtered]);

  useEffect(() => {
    if (criteria.page > 0 && criteria.page >= Math.ceil(filtered.length / recordsPerPage)) {
      setCriteria((prev) => ({ ...prev, page: 0 }));
    }
  }, [criteria.page, filtered.length, recordsPerPage]);

  const handleSaveStock = useCallback(async (payload) => {
    if (!canManageInventory) return false;
    setIsProcessing(true);
    try {
      await StorageService.createStockAction(payload);
      await loadStock();
      UIkit.notification("Stock action saved", "success");
      return true;
    } catch (err) {
      UIkit.notification("Failed to save stock action.", "error");
      return false;
    } finally {
      setIsProcessing(false);
    }
  }, [loadStock, canManageInventory]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / recordsPerPage));
  const pageRows = sorted.slice(
    criteria.page * recordsPerPage,
    criteria.page * recordsPerPage + recordsPerPage,
  );

  return (
    <>
      <section id="dashboard-template">
        <div id="hms-page-title" className="uk-flex uk-flex-between uk-flex-middle">
          <h1 className="uk-heading-small uk-margin-remove">Stock Levels</h1>
        </div>
        <div id="hms-main-body">
          <StorageMenu />
          <div className="uk-flex uk-flex-between uk-flex-top">
            {canManageInventory && (<button
              className="uk-button hms-btn hms-red-btn"
              uk-toggle="target: #modal-storage-stock-add"
              type="button"
              onClick={() => setEditingEntry(null)}
              disabled={opsSubscriptionLocked}
              title={opsSubscriptionLocked ? "Ops Subscription Required" : undefined}
            >
              <span uk-icon="icon: plus; ratio: 0.7"></span> Stock Action
            </button>)}
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <div className="uk-inline uk-margin-small-right">
                    <div className="uk-search uk-search-default uk-form-custom">
                      {criteria.search ? (
                        <button
                          type="button"
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() => handleCriteriaChange("search", "")}
                          aria-label="Clear search"
                        ></button>
                      ) : (
                        <span className="uk-search-icon-flip" uk-search-icon="true" />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Search item, SKU, or location"
                        value={criteria.search}
                        onChange={(event) => handleCriteriaChange("search", event.target.value)}
                      />
                    </div>
                  </div>

                  {[
                    [
                      "location_id",
                      [{ label: "All Locations", value: "" }, ...locations.map((loc) => ({ label: loc.name, value: String(loc.id) }))],
                      "All Locations",
                    ],
                    ["status", STOCK_STATUS_OPTIONS, "All Status"],
                  ].map(([key, options, fallback]) => (
                    <div
                      key={key}
                      className="uk-inline uk-margin-small-right"
                      uk-form-custom="target: > * > span:first-child"
                    >
                      <select
                        value={criteria[key]}
                        onChange={(event) => handleCriteriaChange(key, event.target.value)}
                      >
                        {options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                        <span className="select-value">
                          {criteria[key] ? labelFor(options, criteria[key]) : fallback}
                        </span>
                        <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                      </button>
                    </div>
                  ))}
                </form>
              </Flex>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th>Last Movement</th>
                    <th className="uk-table-expand">Item</th>
                    <th className="uk-table-expand">To Location</th>
                    <th className="uk-table-expand">Location</th>
                    <th>On Hand</th>
                    <th>Available</th>
                    <th>Reserved</th>
                    <th>Damaged</th>
                    <th>Minimum</th>
                    <th>Reorder Qty</th>
                    <th>Inventory Value</th>
                    <th>Replacement Value</th>
                    <th>Status</th>
                    {canManageInventory && (<th className="actions">Actions</th>)}
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={canManageInventory ? 14 : 13}>
                        <div className="uk-text-center uk-padding-small secondary">
                          {stock.length > 0
                            ? "No stock matches the current filters."
                            : stockSource === "error"
                              ? "Stock levels could not be loaded from the API."
                              : "No stock levels returned from the API."}
                        </div>
                      </td>
                    </tr>
                  )}

                  {pageRows.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDateTime(row.last_movement_at)}</td>
                      <td>
                        {row.item_name || optionName(items, row.item_id)}
                        <div className="secondary">{row.sku}</div>
                      </td>
                      <td>
                        {movementDestination(row)}
                        {row.last_to_location_code && (
                          <div className="secondary">{row.last_to_location_code}</div>
                        )}
                      </td>
                      <td className="semi-bold-font">
                        {row.location_name || optionName(locations, row.location_id)}
                        <div className="secondary">{row.location_code || "No code"}</div>
                      </td>

                      <td>{row.on_hand ?? 0}</td>
                      <td>{availableQuantity(row)}</td>
                      <td>{row.reserved ?? 0}</td>
                      <td>{row.damaged ?? 0}</td>
                      <td>{row.minimum_quantity ?? row.reorder_point ?? "-"}</td>
                      <td>{row.reorder_quantity ?? row.par_level ?? "-"}</td>
                      <td>{formatCurrency(row.inventory_value ?? inventoryValue(row, "unit_cost"))}</td>
                      <td>{formatCurrency(row.replacement_value ?? inventoryValue(row, "replacement_cost"))}</td>
                      <td>{statusBadge(row)}</td>
                      {canManageInventory && (<td className="hms-action-btn">
                        <div className="uk-inline">
                          <button
                            className="uk-button"
                            type="button"
                            uk-toggle="target: #modal-storage-stock-add"
                            uk-tooltip="Stock Action"
                            onClick={() => setEditingEntry(row)}
                            disabled={opsSubscriptionLocked}
                            title={opsSubscriptionLocked ? "Ops Subscription Required" : undefined}
                          >
                            <img src="/images/edit.svg" alt="Stock Action" />
                          </button>
                        </div>
                      </td>)}
                    </tr>
                  ))}
                </tbody>
              </table>

              <Flex alignment="between top" className="guest-form-top-filters">
                <Pagination
                  count={totalPages}
                  index={Math.min(criteria.page, totalPages - 1)}
                  onIndexChanged={handleIndexChanged}
                />
                <RecordsPerPage />
              </Flex>
            </div>
          </div>
        </div>
      </section>

      {canManageInventory && (<StorageStockAdjust
        entry={editingEntry}
        locations={locations}
        items={items}
        propertyOptions={propertyOptions}
        reservationOptions={reservationOptions}
        taskOptions={taskOptions}
        flagIsProcessing={isProcessing}
        onSave={handleSaveStock}
        onCancel={() => setEditingEntry(null)}
      />)}
    </>
  );
}
